import { defineStore } from "pinia";
import { ref } from "vue";
import { sortByProperty } from "@/common/sorts";
import { type NavigationItem } from "@/types/NavigationListItem";
import navigation from "@/assets/navigation.json";
import AzureGraphDataFetcher from "@/services/connectors/azureGraphConnector";
import { SidebarItem } from "@/types/SidebarListItem";
import { useAuth } from "@/composables/useAuth";

function convertFontAwesomeIcon(faClass: string): string {
    if (!faClass) return "circle";

    const match = faClass.match(/fa[srb]?\s+fa-(.+)/);
    if (match) {
        return match[1];
    }

    return faClass.replace("fa-", "");
}

function appendChildToParent(parent: SidebarItem, child: SidebarItem): void {
    parent.child ? parent.child.push(child) : (parent.child = [child]);
}

function getItemsHashedById(items: SidebarItem[]): Map<number, SidebarItem> {
    const map = new Map<number, SidebarItem>();
    for (const item of items) {
        map.set(item.id, item);
    }
    return map;
}

function mappingNavigationItem(list: NavigationItem[]): SidebarItem[] {
    return list.map((item) => {
        const iconName = convertFontAwesomeIcon(item.Icon);
        const newItem: SidebarItem = {
            id: item.id,
            parentId: item.CategoryLookupLookupId,
            title: item.Title,
            order: item.Order0,
            icon: {
                element: "font-awesome-icon",
                attributes: { icon: ["fas", iconName] },
            },
            external: true,
        };
        if (item.Url) {
            newItem.href = item.Url;
        }
        return newItem;
    });
}

function getMenuTree(items: SidebarItem[]): SidebarItem[] {
    const map = getItemsHashedById(items);
    const topLevelItems: SidebarItem[] = [];
    for (const item of items) {
        if (!item.parentId) {
            topLevelItems.push(item);
            continue;
        }
        const parent = map.get(item.parentId);
        if (parent) {
            appendChildToParent(parent, item);
        }
    }
    topLevelItems.sort(sortByProperty((x) => x.order));
    return topLevelItems;
}

export const useU3tNavigationStore = defineStore("u3tNavigation", () => {
    let graphFetcher: AzureGraphDataFetcher | null = null;
    const { graphToken } = useAuth();

    const getGraphFetcher = (): AzureGraphDataFetcher => {
        if (!graphFetcher) {
            graphFetcher = new AzureGraphDataFetcher(graphToken.value);
        }
        return graphFetcher;
    };

    const menu = ref<any[]>([]);
    const isLoaded = ref(false);

    const getNavigation = async () => {
        if (isLoaded.value) return;

        try {
            const result = await getGraphFetcher().getNavigation();
            menu.value = result.value.map((item: any) => item.fields);
            isLoaded.value = true;
        } catch (error) {
            menu.value = navigation.value.map((item: any) => item.fields);
            isLoaded.value = true;
            console.error(error);
        }
    };

    // Getter
    const navigationGetter = () => {
        const items = mappingNavigationItem(menu.value);
        return getMenuTree(items);
    };

    return {
        menu,
        isLoaded,
        getNavigation,
        navigation: navigationGetter,
    };
});
